import React, { useState, useEffect, useCallback } from 'react';
import { Archive, Inbox, UserRoundCog, CheckCheck, Plus, Search, MessageSquareQuote, X, Pencil, Undo2, Ban } from 'lucide-react';
import { api, inr, timeAgo, STATUS_STYLES } from '../api';
import { toast } from 'sonner';

function QuickRemarksEditor() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('');

  useEffect(() => { api.get('/quick-remarks').then((r) => setItems(r.data.items)); }, []);

  const save = async (next) => {
    const r = await api.put('/quick-remarks', { items: next });
    setItems(r.data.items);
  };
  const add = () => {
    if (!draft.trim()) return;
    save([...items, draft.trim()]);
    setDraft('');
    toast.success('Quick remark added');
  };
  const remove = (i) => save(items.filter((_, idx) => idx !== i));

  return (
    <div className="mb-3 bg-violet-50 border-2 border-violet-200 rounded-xl px-3 py-2.5" data-testid="quick-remarks-editor">
      <p className="text-[11px] font-bold text-violet-700 mb-2">Quick Remarks — agents tap these to log a remark on a lead in one click</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((it, i) => (
          <span key={i} data-testid={`quick-remark-chip-${i}`}
            className="flex items-center gap-1 bg-white border border-violet-300 text-violet-800 rounded-full pl-2.5 pr-1 py-0.5 text-[11px] font-semibold">
            {it}
            <button data-testid={`quick-remark-delete-${i}`} onClick={() => remove(i)}
              className="p-0.5 rounded-full text-violet-300 hover:text-red-500 hover:bg-red-50"><X size={11} /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-[11px] text-violet-400">No quick remarks yet — add some below</span>}
      </div>
      <div className="flex gap-2">
        <input data-testid="quick-remark-input" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="e.g. Customer will confirm by evening"
          className="flex-1 border border-violet-200 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-violet-500" />
        <button data-testid="quick-remark-add" onClick={add}
          className="bg-violet-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold hover:bg-violet-500">Add</button>
      </div>
    </div>
  );
}

export default function AdminLeadsManager({ agents, onChanged }) {
  const [tab, setTab] = useState('active');
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkAgent, setBulkAgent] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showRemarks, setShowRemarks] = useState(false);
  const [showDirect, setShowDirect] = useState(false);
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [search, setSearch] = useState('');
  const [newLead, setNewLead] = useState({ customer_name: '', mobile_number: '', cart_value: '', assigned_to: '' });
  const [direct, setDirect] = useState({ customer_name: '', mobile_number: '', product_name: '', amount: '', agent_id: '' });

  const load = useCallback(() => {
    api.get(`/leads?archived=${tab === 'archived'}`).then((r) => setLeads(r.data));
    setSelected([]);
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (agents.length && !bulkAgent) setBulkAgent(agents[0].id); }, [agents, bulkAgent]);

  const reassign = async (lead, agentId) => {
    const r = await api.patch(`/leads/${lead.id}`, { assigned_to: agentId });
    toast.success(agentId ? `${lead.customer_name} moved to ${r.data.assigned_to_name}` : `${lead.customer_name} unassigned`);
    load(); onChanged();
  };

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkAssign = async () => {
    if (!selected.length) return toast.error('Select leads first');
    const r = await api.post('/leads/bulk-assign', { lead_ids: selected, agent_id: bulkAgent });
    toast.success(`${r.data.updated} lead(s) restored & assigned to ${r.data.agent_name}`);
    load(); onChanged();
  };

  const bulkClose = async () => {
    if (!selected.length) return toast.error('Select leads first');
    const r = await api.post('/leads/bulk-close', { lead_ids: selected });
    toast.success(`${r.data.updated} lead(s) closed`);
    load(); onChanged();
  };

  const isArchived = tab === 'archived';

  const filtered = leads.filter((l) => {
    if (onlyUnassigned && l.assigned_to) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.customer_name || '').toLowerCase().includes(s)
      || (l.mobile_number || '').includes(s)
      || (l.order_id || '').toLowerCase().includes(s)
      || (l.assigned_to_name || '').toLowerCase().includes(s);
  });

  const editConversion = async (l) => {
    const val = window.prompt(`New converted amount for ${l.customer_name} (₹):`, l.converted_amount);
    if (val === null) return;
    const amt = Number(val);
    if (Number.isNaN(amt) || amt < 0) return toast.error('Enter a valid amount');
    await api.put(`/admin/leads/${l.id}/conversion`, { amount: amt });
    toast.success(`${l.customer_name} — converted amount updated to ${inr(amt)}`);
    load(); onChanged();
  };

  const revertConversion = async (l) => {
    if (!window.confirm(`Revert ${l.customer_name}'s conversion? Revenue credit will be removed and the lead reopens.`)) return;
    await api.delete(`/admin/leads/${l.id}/conversion`);
    toast.success(`${l.customer_name} conversion reverted — lead reopened`);
    load(); onChanged();
  };

  const cancelLead = async (l) => {
    if (!window.confirm(`Cancel lead ${l.customer_name}? It will be closed & archived (any conversion is removed).`)) return;
    await api.post(`/admin/leads/${l.id}/cancel`);
    toast.success(`${l.customer_name} cancelled`);
    load(); onChanged();
  };

  const createDirectOrder = async () => {
    if (!direct.customer_name || !direct.mobile_number || !direct.product_name || !direct.amount || !direct.agent_id) {
      return toast.error('All fields are required for a direct order');
    }
    const ag = agents.find((a) => a.id === direct.agent_id);
    await api.post('/orders', {
      customer_name: direct.customer_name,
      mobile_number: direct.mobile_number,
      products: [{ name: direct.product_name, qty: 1, price: Number(direct.amount) }],
      created_by: direct.agent_id,
      created_by_name: ag?.name || '',
    });
    toast.success(`🎉 Direct order ${inr(direct.amount)} added for ${ag?.name}`);
    setDirect({ customer_name: '', mobile_number: '', product_name: '', amount: '', agent_id: '' });
    setShowDirect(false);
    onChanged();
  };

  const createLead = async () => {
    if (!newLead.customer_name || !newLead.mobile_number) return toast.error('Name & mobile required');
    const r = await api.post('/leads', {
      customer_name: newLead.customer_name,
      mobile_number: newLead.mobile_number,
      cart_value: Number(newLead.cart_value) || 0,
      assigned_to: newLead.assigned_to || null,
      source: 'Manual',
    });
    toast.success(r.data.assigned_to_name ? `Lead created & assigned to ${r.data.assigned_to_name}` : 'Lead created (unassigned)');
    setNewLead({ customer_name: '', mobile_number: '', cart_value: '', assigned_to: '' });
    setShowNew(false);
    load(); onChanged();
  };

  return (
    <div data-testid="admin-leads-manager">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h3 className="font-display font-bold text-lg text-slate-800">Leads Manager</h3>
        <button data-testid="admin-new-lead-button" onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-1 bg-emerald-600 text-white rounded-full px-3 py-1 text-xs font-bold hover:bg-emerald-500 transition-colors">
          <Plus size={12} /> New Lead
        </button>
        <div className="flex bg-slate-100 border-2 border-slate-200 rounded-full p-0.5">
          <button data-testid="admin-leads-tab-active" onClick={() => setTab('active')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-colors ${!isArchived ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
            <Inbox size={12} /> Active
          </button>
          <button data-testid="admin-leads-tab-archived" onClick={() => setTab('archived')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-colors ${isArchived ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500'}`}>
            <Archive size={12} /> Archived
          </button>
        </div>
        <button data-testid="admin-direct-order-button" onClick={() => setShowDirect(!showDirect)}
          className="flex items-center gap-1 bg-amber-500 text-white rounded-full px-3 py-1 text-xs font-bold hover:bg-amber-400 transition-colors">
          <Plus size={12} /> Direct Order
        </button>
        <button data-testid="quick-remarks-toggle" onClick={() => setShowRemarks(!showRemarks)}
          className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold transition-colors ${showRemarks ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700 hover:bg-violet-200'}`}>
          <MessageSquareQuote size={12} /> Quick Remarks
        </button>
        <button data-testid="admin-filter-unassigned" onClick={() => setOnlyUnassigned(!onlyUnassigned)}
          className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold border-2 transition-colors ${onlyUnassigned ? 'bg-rose-600 border-rose-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300'}`}>
          Unassigned {leads.filter((l) => !l.assigned_to).length}
        </button>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-leads-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, order, agent…"
            className="w-full bg-white border-2 border-slate-200 rounded-full pl-8 pr-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder:text-slate-400" />
        </div>
        <p className="font-hand text-base text-slate-400">leads auto-archive after 72h if not closed</p>
      </div>

      {showRemarks && <QuickRemarksEditor />}

      {showNew && (
        <div className="flex items-center gap-2 mb-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl px-3 py-2.5 flex-wrap" data-testid="admin-new-lead-form">
          <input data-testid="admin-new-lead-name" placeholder="Customer name" value={newLead.customer_name}
            onChange={(e) => setNewLead({ ...newLead, customer_name: e.target.value })}
            className="border border-emerald-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500 w-40" />
          <input data-testid="admin-new-lead-mobile" placeholder="Mobile" value={newLead.mobile_number}
            onChange={(e) => setNewLead({ ...newLead, mobile_number: e.target.value })}
            className="border border-emerald-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500 w-32" />
          <input data-testid="admin-new-lead-value" placeholder="Cart ₹" type="number" value={newLead.cart_value}
            onChange={(e) => setNewLead({ ...newLead, cart_value: e.target.value })}
            className="border border-emerald-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500 w-24" />
          <select data-testid="admin-new-lead-agent" value={newLead.assigned_to}
            onChange={(e) => setNewLead({ ...newLead, assigned_to: e.target.value })}
            className="border border-emerald-200 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500">
            <option value="">Auto-assign (round-robin)</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button data-testid="admin-new-lead-submit" onClick={createLead}
            className="bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-xs font-bold hover:bg-emerald-500">Create Lead</button>
        </div>
      )}

      {showDirect && (
        <div className="flex items-center gap-2 mb-3 bg-amber-50 border-2 border-amber-200 rounded-xl px-3 py-2.5 flex-wrap" data-testid="direct-order-form">
          <input data-testid="direct-order-name" placeholder="Customer name" value={direct.customer_name}
            onChange={(e) => setDirect({ ...direct, customer_name: e.target.value })}
            className="border border-amber-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500 w-36" />
          <input data-testid="direct-order-mobile" placeholder="Mobile" value={direct.mobile_number}
            onChange={(e) => setDirect({ ...direct, mobile_number: e.target.value })}
            className="border border-amber-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500 w-28" />
          <input data-testid="direct-order-product" placeholder="Product name" value={direct.product_name}
            onChange={(e) => setDirect({ ...direct, product_name: e.target.value })}
            className="border border-amber-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500 w-40" />
          <input data-testid="direct-order-amount" placeholder="Amount ₹" type="number" value={direct.amount}
            onChange={(e) => setDirect({ ...direct, amount: e.target.value })}
            className="border border-amber-200 rounded-lg px-2 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-amber-500 w-24" />
          <select data-testid="direct-order-agent" value={direct.agent_id}
            onChange={(e) => setDirect({ ...direct, agent_id: e.target.value })}
            className="border border-amber-200 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-500">
            <option value="">Select agent…</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button data-testid="direct-order-submit" onClick={createDirectOrder}
            className="bg-amber-500 text-white rounded-lg px-3 py-1.5 text-xs font-bold hover:bg-amber-400">Add Order 🎉</button>
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex items-center gap-2 mb-3 bg-slate-800 text-white rounded-xl px-3 py-2 flex-wrap" data-testid="bulk-actions-bar">
          <span className="text-xs font-bold">{selected.length} selected</span>
          <select data-testid="bulk-agent-select" value={bulkAgent} onChange={(e) => setBulkAgent(e.target.value)}
            className="bg-slate-700 text-white rounded-lg px-2 py-1 text-xs focus:outline-none">
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button data-testid="bulk-assign-button" onClick={bulkAssign}
            className="flex items-center gap-1 bg-yellow-400 text-yellow-950 rounded-lg px-3 py-1 text-xs font-bold hover:bg-yellow-300 transition-colors">
            <UserRoundCog size={12} /> {isArchived ? 'Assign & Restore' : 'Assign Selected'}
          </button>
          <button data-testid="bulk-close-button" onClick={bulkClose}
            className="flex items-center gap-1 bg-emerald-500 text-white rounded-lg px-3 py-1 text-xs font-bold hover:bg-emerald-400 transition-colors">
            <CheckCheck size={12} /> Close Leads
          </button>
          <button onClick={() => setSelected([])} className="text-[11px] text-slate-300 hover:text-white ml-auto">clear</button>
        </div>
      )}
      {selected.length === 0 && filtered.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <button data-testid="select-all-button" onClick={() => setSelected(filtered.map((l) => l.id))}
            className="text-[11px] font-bold text-slate-400 hover:text-slate-700">☑ select all {filtered.length} shown (for bulk assign)</button>
        </div>
      )}

      <div className="bg-white border-2 border-slate-200 rounded-xl sticky-shadow overflow-hidden">
        <div className="max-h-80 overflow-y-auto scroll-thin divide-y divide-slate-100" data-testid="admin-leads-list">
          {filtered.length === 0 && (
            <p className="font-hand text-lg text-slate-400 text-center py-8">
              {search ? `No leads matching “${search}”` : isArchived ? 'No archived leads — team is on fire! 🔥' : 'No active leads'}
            </p>
          )}
          {filtered.map((l) => (
            <div key={l.id} data-testid={`admin-lead-row-${l.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
              <input type="checkbox" data-testid={`bulk-select-${l.id}`} checked={selected.includes(l.id)}
                onChange={() => toggleSelect(l.id)} className="accent-slate-800 cursor-pointer" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-900 truncate">
                  {l.customer_name}
                  <span className="ml-2 font-medium text-slate-400">{l.order_id}</span>
                  {l.merged_orders?.length > 0 && (
                    <span className="ml-2 text-[9px] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">⊕ {l.merged_orders.length} merged</span>
                  )}
                </p>
                <p className="text-[10px] text-slate-400">{l.mobile_number} · {inr(l.cart_value)} · {isArchived ? `archived ${timeAgo(l.archived_at)}` : timeAgo(l.created_at)}</p>
              </div>
              <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[l.status]}`}>
                {l.converted ? '✓ Won' : l.status}
              </span>
              {l.auto_converted && (
                <span data-testid={`auto-convert-badge-${l.id}`} title={l.closed_note || 'Auto Convert'}
                  className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-teal-50 border-teal-300 text-teal-700">
                  🤖 Auto Convert
                </span>
              )}
              {l.converted && (
                <span className="shrink-0 flex items-center gap-0.5">
                  <button data-testid={`edit-conversion-${l.id}`} onClick={() => editConversion(l)}
                    title="Edit converted amount"
                    className="p-1 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil size={13} /></button>
                  <button data-testid={`revert-conversion-${l.id}`} onClick={() => revertConversion(l)}
                    title="Revert conversion (remove revenue credit, reopen lead)"
                    className="p-1 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50"><Undo2 size={13} /></button>
                </span>
              )}
              {!isArchived && (
                <button data-testid={`cancel-lead-${l.id}`} onClick={() => cancelLead(l)}
                  title="Cancel this lead (close & archive)"
                  className="shrink-0 p-1 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50"><Ban size={13} /></button>
              )}
              <select
                data-testid={`admin-lead-agent-select-${l.id}`}
                value={l.assigned_to || ''}
                onChange={(e) => reassign(l, e.target.value)}
                className="shrink-0 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
              >
                <option value="">Unassigned</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
